import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import Seo, { breadcrumbs } from "../components/Seo";
import { absUrl } from "../lib/seo";
import { MetaLabel, Wordmark } from "../brand/Brand";
import { useAuth } from "../auth/AuthContext";
import { deleteAccount } from "../lib/users";

const LAST_UPDATED = "July 2026";
const SUPPORT_EMAIL = "jm@bboycreative.com";
const CHARGEBACK_THRESHOLD = "$800";

const PAGES = {
  terms: {
    slug: "terms",
    path: "/legal/terms",
    title: "Terms of Service",
    meta: "01 · LEGAL · TERMS",
    intro:
      "These terms govern your use of bboy creative's services, products, and properties. Plain language where we can manage it — binding either way.",
    sections: [
      {
        h: "01 · The agreement",
        p: [
          "By engaging bboy creative for design, development, or other work, by purchasing any product or service, or by using bboycreative.com or our apps, you agree to these terms. If you don't agree, don't use the services.",
          "We sign separate Statements of Work (SOWs) for paid engagements. The SOW controls scope, fees, and timelines. These terms cover everything else and apply alongside the SOW.",
        ],
      },
      {
        h: "02 · Scope and changes",
        p: [
          "We deliver what's listed in the SOW. New requests outside that scope get logged, estimated, and added with your approval before we start.",
          "We aim to ship in fixed windows. Delays caused by missing inputs (assets, approvals, access) shift the window accordingly.",
        ],
      },
      {
        h: "03 · Fees and payment",
        p: [
          "Invoices are due Net 14 unless the SOW says otherwise. Late balances accrue 1.5% per month or the legal maximum, whichever is lower.",
          "Milestone work is billed when each milestone is delivered. Subscription work is billed monthly in advance. Products are charged at the time of purchase.",
          "You authorize us and our third-party payment processors to charge the payment method you provide for all amounts you incur.",
        ],
      },
      {
        h: "04 · Purchases, refunds & chargebacks",
        p: [
          "All sales are final. We do not issue refunds of any kind on any purchase — services, subscriptions, milestone payments, digital goods, or physical goods.",
          "We stand behind our work. Instead of refunds, we make you whole: we will re-perform, repair, replace, or issue account credit to resolve a legitimate issue. Reach out and we'll fix it.",
          `Initiating a chargeback or payment dispute instead of contacting us first is a breach of these terms. For any chargeback or payment dispute exceeding ${CHARGEBACK_THRESHOLD}, we will pursue recovery in the state or federal courts located in Bexar County, Texas, and will seek the disputed amount plus our costs and reasonable attorneys' fees.`,
          "The complete policy — including how we make things right and how disputes are handled — lives on the Refunds & Chargebacks page.",
        ],
      },
      {
        h: "05 · Acceptable use",
        p: [
          "Don't use the services to break the law, infringe others' rights, upload malware, scrape or overload our systems, reverse-engineer our tools, or resell access without permission.",
          "We may suspend or terminate access for conduct that violates these terms or puts our users, systems, or business at risk.",
        ],
      },
      {
        h: "06 · Ownership",
        p: [
          "Once you've paid for the work in full, you own the deliverables — code, designs, copy. We keep a license to display the work in our portfolio unless you ask us not to in writing.",
          "We retain ownership of any internal tools, libraries, frameworks, or templates we used to build your project.",
        ],
      },
      {
        h: "07 · Confidentiality",
        p: [
          "We keep your business information confidential. We expect the same in return for our methods, pricing, and code. We're happy to sign a mutual NDA before we start.",
        ],
      },
      {
        h: "08 · Warranty and liability",
        p: [
          "We deliver work to industry standards. We don't warrant that software is bug-free or that any third-party platform (app stores, payment networks, and infrastructure providers) will behave a particular way. Services and products are otherwise provided “as is” to the fullest extent permitted by law.",
          "Our total liability for any claim is capped at the fees you paid us in the prior three months. We're not liable for indirect, incidental, consequential, or lost-profit damages.",
        ],
      },
      {
        h: "09 · Termination",
        p: [
          "Either party can terminate an SOW with 14 days' written notice. You pay for work completed up to the termination date. We hand over current artifacts. Amounts already paid are non-refundable, consistent with our refund policy.",
          "We may pause or terminate access to our sites or apps at any time if these terms are abused.",
        ],
      },
      {
        h: "10 · Governing law & disputes",
        p: [
          "These terms are governed by the laws of the State of Texas, without regard to conflict-of-laws rules.",
          "Except where a party seeks urgent injunctive relief, any dispute is resolved exclusively in the state or federal courts located in Bexar County, Texas, and you consent to that jurisdiction and venue.",
        ],
      },
    ],
  },

  refunds: {
    slug: "refunds",
    path: "/legal/refunds",
    title: "Refunds & Chargebacks",
    meta: "02 · LEGAL · REFUNDS",
    intro:
      "Our policy in one line: all sales are final, we don't do refunds — and we will always make you whole another way. Here's exactly how that works.",
    sections: [
      {
        h: "01 · All sales are final",
        p: [
          "Every purchase from bboy creative is final at the time of sale. This applies to all purchases of any kind: professional services, project milestones, retainers and subscriptions, digital products and downloads, and physical goods.",
          "We do not issue refunds, partial refunds, or pro-rated refunds. By completing a purchase you acknowledge and accept this no-refund policy.",
        ],
      },
      {
        h: "02 · We make it right",
        p: [
          "“No refunds” does not mean “no recourse.” If something is wrong, we make you whole. Depending on the situation, that means we will re-perform the work, repair or fix the deliverable, replace a defective item, or issue account credit toward current or future work.",
          "Our goal is that you end up with what you paid for. We would rather earn your trust by fixing the problem than walk away from it.",
        ],
      },
      {
        h: "03 · How to raise an issue",
        p: [
          `If a purchase isn't right, contact us first at ${SUPPORT_EMAIL} with your order or invoice reference and a description of the problem. Most issues are resolved quickly once we know about them.`,
          "Please give us the chance to make it right before escalating to your bank or card network — a dispute filed without contacting us slows the fix down and, past a certain amount, has legal consequences (below).",
        ],
      },
      {
        h: "04 · Chargebacks & payment disputes",
        p: [
          "Filing a chargeback or payment dispute in place of contacting us is a breach of our Terms of Service. Because our services and goods are delivered as agreed and all sales are final, we treat wrongful chargebacks as an attempt to obtain services or goods without paying.",
          "When a dispute is filed, we will respond to the card network or processor with our records — order details, delivery confirmation, communications, and this policy — to have the charge upheld.",
          `For any chargeback or payment dispute that exceeds ${CHARGEBACK_THRESHOLD}, we will pursue recovery through legal action in the state or federal courts located in Bexar County, Texas. We will seek the full disputed amount together with our court costs, collection costs, and reasonable attorneys' fees.`,
        ],
      },
      {
        h: "05 · Subscriptions & retainers",
        p: [
          "You can cancel a subscription or retainer at any time. Cancellation stops future billing; it does not refund the current or past billing periods. You keep access through the end of the period you've paid for.",
        ],
      },
      {
        h: "06 · Digital & physical goods",
        p: [
          "Digital products and downloads are non-returnable and non-refundable once access or delivery has been provided.",
          "If a physical item arrives defective or damaged, contact us within a reasonable time and we will replace it or repair it — we do not issue cash refunds, consistent with this policy.",
        ],
      },
      {
        h: "07 · Where the law requires more",
        p: [
          "Some jurisdictions grant consumers non-waivable rights that a policy cannot remove. Nothing here limits any right you have that cannot be limited by law. Where a mandatory remedy applies, we honor it to the minimum extent the law requires.",
        ],
      },
      {
        h: "08 · Questions",
        p: [`Email ${SUPPORT_EMAIL}. We read every message and want to resolve issues, not fight about them.`],
      },
    ],
  },

  privacy: {
    slug: "privacy",
    path: "/legal/privacy",
    title: "Privacy Policy",
    meta: "03 · LEGAL · PRIVACY",
    intro:
      "We collect the minimum we need to run the studio, ship your work, and process your purchases. Here's exactly what we collect, why, who touches it, and the choices you have.",
    sections: [
      {
        h: "01 · What we collect",
        p: [
          "Contact details you give us (name, email, company, phone) when you fill out the intake form, message us, or create an account.",
          "Account data — your role (client / admin), the projects you're attached to, messages, and files you upload through the dashboard or app.",
          "Purchase & transaction data — what you bought, amounts, dates, invoice and order history, and limited billing metadata. We do not store full payment-card numbers; those are handled by our payment processors.",
          "Device & technical data — IP address, device and browser type, app version, and, on mobile, push-notification tokens and device identifiers used to deliver notifications.",
          "Usage & diagnostics — pages or screens visited, actions taken, and crash/error reports, used for security, reliability, and analytics.",
        ],
      },
      {
        h: "02 · Why we collect it",
        p: [
          "To respond to inquiries, scope projects, and run engagements.",
          "To operate the account experience — projects, messages, invoices, and purchases — and to deliver, support, and secure what you bought.",
          "To process payments and to detect, prevent, and investigate fraud, abuse, and payment disputes.",
          "To improve our products, meet legal and tax obligations, and enforce our terms.",
        ],
      },
      {
        h: "03 · Payment data",
        p: [
          "Payments are processed by our third-party payment processors. When you pay, your card or account details go directly to those processors under their own security and privacy terms. We receive a confirmation and limited metadata (last four digits, card brand, billing region, transaction ID) — not your full card number.",
          "We retain transaction records we need for accounting, tax, warranty, and dispute purposes.",
        ],
      },
      {
        h: "04 · Who we share it with",
        p: [
          "Service providers that run the business: our cloud infrastructure provider (hosting, authentication, database, storage, push, crash reporting), our payment processors, analytics providers (aggregate traffic and product usage), and email providers (transactional and account mail).",
          "Each provider only receives the data it needs to perform its function, and only to provide services to us. We do not sell your personal information, and we do not “share” it for cross-context behavioral advertising.",
          "We may disclose information when required by law, to enforce our terms, to collect amounts owed, or to defend our legal rights — including providing records to card networks and courts in a payment dispute.",
        ],
      },
      {
        h: "05 · Automated processing",
        p: [
          "We use automated checks to screen for fraud and payment abuse (for example, flagging unusual orders or chargeback risk). These checks inform human decisions; we do not make decisions producing legal effects about you based solely on automated processing without a person in the loop.",
        ],
      },
      {
        h: "06 · How long we keep it",
        p: [
          "Account and project records: for the life of the relationship plus up to seven years for tax, warranty, and legal purposes.",
          "Transaction and invoice records: retained as required by tax and accounting law (generally seven years).",
          "Diagnostic logs: typically 90 days, then aggregated or discarded. Marketing email: until you unsubscribe.",
        ],
      },
      {
        h: "07 · International transfers",
        p: [
          "We are based in the United States and our providers store data primarily in the U.S. If you access the services from outside the U.S., you understand your data will be transferred to and processed in the U.S. Where required, we rely on appropriate safeguards such as Standard Contractual Clauses.",
        ],
      },
      {
        h: "08 · Your choices & rights",
        p: [
          `We store your personal data only as long as we need it to run the services, and you can have it removed at your discretion. You can ask us to access, correct, export, or delete your personal data, and to opt out of marketing. Email ${SUPPORT_EMAIL} and we respond within 30 days. You can also delete your account yourself from the Your Data page.`,
          "California residents have rights under the CCPA/CPRA (know, delete, correct, and opt out of sale/share — we do not sell or share). EU/UK residents have rights under the GDPR/UK GDPR (access, rectification, erasure, restriction, portability, objection). We honor these on request and won't discriminate against you for exercising them.",
        ],
      },
      {
        h: "09 · Security & breach notice",
        p: [
          "Data in transit is encrypted with TLS; data at rest is encrypted by our cloud providers. Access to production data is limited to studio principals and protected by two-factor authentication.",
          "If we experience a breach that affects your personal data, we will notify affected users and any required authorities without undue delay and, where applicable, within 72 hours of confirming it.",
        ],
      },
      {
        h: "10 · Children",
        p: [
          "Our services are not directed to children under 13, and we do not knowingly collect their personal data. If you believe a child has provided us data, contact us and we will delete it.",
        ],
      },
      {
        h: "11 · Changes",
        p: [
          "If we materially change this policy, we'll post a notice at the top of this page and email account holders. The latest revision date is shown at the top.",
        ],
      },
    ],
  },

  cookies: {
    slug: "cookies",
    path: "/legal/cookies",
    title: "Cookie Policy",
    meta: "04 · LEGAL · COOKIES",
    intro:
      "We use a small set of cookies and similar technologies. Here's the full inventory and how to control them.",
    sections: [
      {
        h: "01 · What cookies are",
        p: [
          "Cookies are small text files set by a site (or a service the site embeds) and stored in your browser. On mobile, comparable technologies include local storage and device identifiers. They let us keep you signed in, remember preferences, and measure traffic.",
        ],
      },
      {
        h: "02 · Strictly necessary",
        p: [
          "Authentication and session cookies set by our authentication provider keep you signed in and secure the dashboard. These cannot be disabled while using authenticated areas.",
        ],
      },
      {
        h: "03 · Analytics",
        p: [
          "Analytics cookies set by our analytics provider measure aggregate visits and page paths. We don't use them to identify individuals.",
          "You can opt out with a tracking-blocking browser or your browser's own cookie controls.",
        ],
      },
      {
        h: "04 · Functional",
        p: [
          "Local-storage entries that remember your theme, dismissed banners, or draft form inputs. Set by us, not third parties.",
        ],
      },
      {
        h: "05 · Managing cookies",
        p: [
          "Most browsers let you block or delete cookies in settings. If you block strictly necessary cookies, signed-in features will not work.",
        ],
      },
    ],
  },

  data: {
    slug: "data",
    path: "/legal/your-data",
    title: "Your Data",
    meta: "05 · LEGAL · YOUR DATA",
    intro:
      "A practical guide to seeing, exporting, or deleting the data we hold on you — including deleting your account yourself, right here.",
    sections: [
      {
        h: "01 · What you can request",
        p: [
          "Access — a copy of the personal data we hold about you.",
          "Correction — fix anything that's wrong.",
          "Export — a portable copy in JSON or CSV.",
          "Deletion — remove your account and associated personal data, subject to records we're legally required to keep (invoices, tax, and transaction records).",
          "Opt-out — stop receiving marketing email or analytics tracking.",
        ],
      },
      {
        h: "02 · How to request it",
        p: [
          `Email ${SUPPORT_EMAIL} from the address on your account, with the request and any account references. You can also use the self-serve controls below.`,
          "We may ask for additional verification if the request is unusual or doesn't come from the account email. We respond within 30 days; complex requests may take up to 60, and we'll let you know.",
        ],
      },
      {
        h: "03 · Delete your account",
        p: [
          "You can delete your account and personal data yourself, below. Deleting your account signs you out and permanently removes your profile, saved details, messages, and uploads.",
          "Some records must be retained even after deletion — invoices, payment/transaction history, and tax records — for as long as the law requires. Deletion does not cancel outstanding balances or waive amounts you owe.",
          "This action can't be undone. If you'd rather we handle it, email us and we'll process the deletion for you.",
        ],
      },
      {
        h: "04 · Where your data lives",
        p: [
          "Our cloud infrastructure provider — primary database, file storage, authentication, and push. Hosted in the United States.",
          "Our payment processors — payment records and invoice history.",
          "Analytics and email providers — aggregate usage and mail logs.",
        ],
      },
      {
        h: "05 · Security",
        p: [
          "Data in transit is encrypted with TLS. Data at rest is encrypted by our cloud providers. Access to production data is limited to studio principals and protected by 2FA.",
          "If we suffer a breach affecting your data, we'll notify you without undue delay, and within 72 hours of confirming it where the law requires.",
        ],
      },
      {
        h: "06 · Questions",
        p: [`Email ${SUPPORT_EMAIL}. We aim to reply same-day during business hours.`],
      },
    ],
  },
};

const NAV = [
  { slug: "terms", label: "Terms of Service" },
  { slug: "refunds", label: "Refunds & Chargebacks" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "cookies", label: "Cookie Policy" },
  { slug: "data", label: "Your Data" },
];

/* ── Self-serve account controls (rendered on the Your Data page) ─────────── */

const AccountActions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  const cardStyle = {
    marginTop: 24,
    border: "1px solid var(--fw-line-strong)",
    borderRadius: 4,
    padding: 24,
    background: "var(--fw-cream-2)",
    maxWidth: 720,
  };

  if (!user) {
    return (
      <div style={cardStyle}>
        <MetaLabel>◆ Account deletion</MetaLabel>
        <p style={{ fontSize: 15, lineHeight: 1.7, color: "var(--fw-ink)", margin: "12px 0 16px" }}>
          Sign in to delete your account and data directly. Not signed in?{" "}
          <Link to="/login" style={{ color: "var(--fw-red)", fontWeight: 600 }}>
            Sign in
          </Link>{" "}
          first, or email {SUPPORT_EMAIL} and we'll process it for you.
        </p>
      </div>
    );
  }

  const onDelete = async () => {
    setError(null);
    setBusy(true);
    try {
      await deleteAccount();
      navigate("/", { replace: true });
    } catch (e) {
      const code = e?.code || "";
      if (code.includes("requires-recent-login")) {
        setError(
          "For your security, deleting an account requires a recent sign-in. Please sign out, sign back in, and try again."
        );
      } else {
        setError(e?.message || "We couldn't delete the account. Email " + SUPPORT_EMAIL + " and we'll handle it.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={cardStyle}>
      <MetaLabel>◆ Danger zone</MetaLabel>
      <h3
        style={{
          fontFamily: "var(--fw-sans)",
          fontWeight: 700,
          fontSize: 20,
          letterSpacing: "-0.02em",
          margin: "10px 0 6px",
          color: "var(--fw-ink)",
        }}
      >
        Delete your account
      </h3>
      <p style={{ fontSize: 15, lineHeight: 1.7, color: "var(--fw-ink)", margin: "0 0 16px" }}>
        Permanently removes your profile, saved details, messages, and uploads. Invoices and
        transaction records are retained where the law requires. This can't be undone.
      </p>

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            fontFamily: "var(--fw-mono)",
            fontSize: 11,
            letterSpacing: "1.4px",
            textTransform: "uppercase",
            padding: "12px 20px",
            border: "1px solid var(--fw-red)",
            background: "transparent",
            color: "var(--fw-red)",
            cursor: "pointer",
          }}
        >
          Delete my account
        </button>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ fontSize: 14, color: "var(--fw-ink)" }}>
            Type <strong>DELETE</strong> to confirm:
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoFocus
              style={{
                display: "block",
                marginTop: 8,
                padding: "10px 12px",
                border: "1px solid var(--fw-line-strong)",
                borderRadius: 4,
                fontFamily: "var(--fw-mono)",
                fontSize: 14,
                width: 220,
                background: "var(--fw-cream)",
                color: "var(--fw-ink)",
              }}
            />
          </label>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              disabled={confirmText !== "DELETE" || busy}
              onClick={onDelete}
              style={{
                fontFamily: "var(--fw-mono)",
                fontSize: 11,
                letterSpacing: "1.4px",
                textTransform: "uppercase",
                padding: "12px 20px",
                border: "none",
                background: "var(--fw-red)",
                color: "var(--fw-cream)",
                cursor: confirmText === "DELETE" && !busy ? "pointer" : "not-allowed",
                opacity: confirmText === "DELETE" && !busy ? 1 : 0.5,
              }}
            >
              {busy ? "Deleting…" : "Permanently delete"}
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setConfirmText("");
                setError(null);
              }}
              style={{
                fontFamily: "var(--fw-mono)",
                fontSize: 11,
                letterSpacing: "1.4px",
                textTransform: "uppercase",
                padding: "12px 20px",
                border: "1px solid var(--fw-line-strong)",
                background: "transparent",
                color: "var(--fw-ink)",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
          {error && (
            <p style={{ color: "var(--fw-red)", fontSize: 14, margin: 0 }}>{error}</p>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Page ────────────────────────────────────────────────────────────────── */

const LegalPage = ({ slug }) => {
  const page = PAGES[slug];
  if (!page) return null;
  return (
    <div style={{ background: "var(--fw-cream)", minHeight: "100vh" }}>
      <Seo
        title={`${page.title} — bboy creative`}
        description={page.intro}
        path={page.path}
        jsonLd={[
          {
            "@type": "WebPage",
            "@id": absUrl(page.path),
            name: page.title,
            url: absUrl(page.path),
            // Tells Google this is boilerplate policy text, not editorial
            // content competing for rankings.
            isPartOf: { "@type": "WebSite", "@id": absUrl("/") },
          },
          breadcrumbs([
            { name: "Home", path: "/" },
            { name: page.title, path: page.path },
          ]),
        ]}
      />
      <Navigation />

      <main
        style={{
          maxWidth: 1440,
          margin: "0 auto",
          padding: "80px 40px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "240px 1fr",
            gap: 64,
          }}
          className="fw-legal-grid"
        >
          <aside>
            <MetaLabel>§ Legal</MetaLabel>
            <nav
              style={{
                marginTop: 16,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                position: "sticky",
                top: 96,
              }}
            >
              {NAV.map((n) => {
                const active = n.slug === slug;
                return (
                  <Link
                    key={n.slug}
                    to={PAGES[n.slug].path}
                    style={{
                      padding: "10px 0",
                      borderTop: "1px solid var(--fw-line)",
                      textDecoration: "none",
                      fontFamily: "var(--fw-mono)",
                      fontSize: 11,
                      letterSpacing: "1.4px",
                      textTransform: "uppercase",
                      color: active ? "var(--fw-ink)" : "var(--fw-muted)",
                      borderLeft: active
                        ? "1.5px solid var(--fw-red)"
                        : "1.5px solid transparent",
                      paddingLeft: 12,
                    }}
                  >
                    {n.label}
                  </Link>
                );
              })}
            </nav>
          </aside>

          <article>
            <MetaLabel>{page.meta}</MetaLabel>
            <h1
              style={{
                fontFamily: "var(--fw-sans)",
                fontWeight: 800,
                letterSpacing: "-0.04em",
                lineHeight: 0.95,
                fontSize: "clamp(48px, 7vw, 88px)",
                margin: "16px 0 8px",
                color: "var(--fw-ink)",
              }}
            >
              {page.title}
              <span style={{ color: "var(--fw-red)" }}>.</span>
            </h1>
            <div
              style={{
                fontFamily: "var(--fw-mono)",
                fontSize: 11,
                letterSpacing: "1.4px",
                textTransform: "uppercase",
                color: "var(--fw-muted)",
                marginBottom: 32,
              }}
            >
              Last updated · {LAST_UPDATED}
            </div>

            <p
              style={{
                fontSize: 18,
                lineHeight: 1.6,
                color: "var(--fw-ink)",
                maxWidth: 720,
                marginBottom: 48,
              }}
            >
              {page.intro}
            </p>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 40,
                maxWidth: 720,
              }}
            >
              {page.sections.map((s) => (
                <section key={s.h}>
                  <h2
                    style={{
                      fontFamily: "var(--fw-sans)",
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                      fontSize: 24,
                      margin: "0 0 12px",
                      color: "var(--fw-ink)",
                    }}
                  >
                    {s.h}
                  </h2>
                  {s.p.map((para, i) => (
                    <p
                      key={i}
                      style={{
                        fontSize: 15,
                        lineHeight: 1.7,
                        color: "var(--fw-ink)",
                        margin: "0 0 12px",
                      }}
                    >
                      {para}
                    </p>
                  ))}
                </section>
              ))}
            </div>

            {slug === "data" && <AccountActions />}

            <div
              style={{
                marginTop: 80,
                paddingTop: 32,
                borderTop: "1px solid var(--fw-line)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 16,
              }}
            >
              <Wordmark size={20} />
              <span
                style={{
                  fontFamily: "var(--fw-mono)",
                  fontSize: 10,
                  letterSpacing: "1.2px",
                  textTransform: "uppercase",
                  color: "var(--fw-muted)",
                }}
              >
                Questions? {SUPPORT_EMAIL}
              </span>
            </div>
          </article>
        </div>
      </main>

      <style>{`
        @media (max-width: 768px) {
          .fw-legal-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
        }
      `}</style>
    </div>
  );
};

export const TermsOfService = () => <LegalPage slug="terms" />;
export const RefundPolicy = () => <LegalPage slug="refunds" />;
export const PrivacyPolicy = () => <LegalPage slug="privacy" />;
export const CookiePolicy = () => <LegalPage slug="cookies" />;
export const YourData = () => <LegalPage slug="data" />;

export default LegalPage;
